import React, { createContext, useContext, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchProfile } from '../store/slices/profileSlice';
import type { Profile } from '../store/slices/profileSlice';

interface ProfileContextValue {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  refetchProfile: () => void;
}

export const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
}

/**
 * ProfileProvider component that manages profile state and provides it to the application.
 * This provider fetches the profile once on mount and makes it available to all child components.
 * 
 * @example
 * <ProfileProvider>
 *   <App />
 * </ProfileProvider>
 */
export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const profile: Profile = useAppSelector((state) => state.profile.profile);
  const loading = useAppSelector((state) => state.profile.loading);
  const error = useAppSelector((state) => state.profile.error);

  const refetchProfile = useCallback(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  // Auto-fetch profile on mount if not already loaded
  useEffect(() => {
    if (!profile?.nuid && !loading) {
      refetchProfile();
    }
  }, []); // Empty deps - only run once on mount

  const value = useMemo(() => ({
    profile: profile?.nuid ? profile : null,
    loading,
    error,
    refetchProfile,
  }), [profile, loading, error, refetchProfile]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

/**
 * Custom hook to access profile context.
 * Must be used within a ProfileProvider.
 * 
 * @throws {Error} If used outside ProfileProvider
 * @returns {ProfileContextValue} The profile context value containing profile data, loading state, error, and refetch function
 * 
 * @example
 * const { profile, loading, error, refetchProfile } = useProfile();
 */
export const useProfile = (): ProfileContextValue => {
  const context = useContext(ProfileContext);

  // Provide a safe fallback for tests so components can be rendered without
  // needing to wrap every test in the full provider tree.
  if (context === undefined) {
    if (process.env.NODE_ENV === 'test') {
      return {
        profile: null,
        loading: false,
        error: null,
        refetchProfile: () => {},
      };
    }

    throw new Error('useProfile must be used within a ProfileProvider');
  }

  return context;
};
